import React, { useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { CartStatusEnum } from '@pegasus/cart/components/expired-overlay';
import { isWebpushVariant } from '@shared/abtests/webPush';
import { sendPushNotification as sendPushNotificationAction } from '@client/redux/modules/pushNotification/actions';
import { ConfigContext } from '@client/hoc/config/ConfigContext';
import { isSafari } from '@client/helpers/browser';
import { getTabVisible } from '@client/redux/modules/clientDevice/selectors';
import { getFirstName } from '@client/redux/modules/customer/selectors';
import { getIsOptedIn } from '@client/redux/modules/pushNotification/selectors';
import { getExternalCartStatus } from './utils';

const cartNotification = {
    [CartStatusEnum.CART_EXPIRE_IN_5_MINUTES]: {
        prefix: 'expiresSoon',
        ttl: 4 * 60
    },
    [CartStatusEnum.CART_EXPIRE_IN_SHORT_PERIOD]: {
        prefix: 'expiresSoon',
        ttl: 1 * 60
    },
    [CartStatusEnum.CART_EXPIRED]: {
        prefix: 'expired',
        ttl: -1
    }
};

export const usePushNotifications = () => {
    const intl = useIntl();
    const isWebpush = useSelector(isWebpushVariant);
    const isTabVisible = useSelector(getTabVisible);
    const firstName = useSelector(getFirstName);
    const optedIn = useSelector(getIsOptedIn);
    const dispatch = useDispatch();
    const { config } = useContext(ConfigContext);
    const hasExternalCartNotification = !!getExternalCartStatus();

    const isEnabled = config.isEnabled('pushNotification')
        && !isSafari()
        && isWebpush;

    const triggerNotification = useCallback((cartStatus: CartStatusEnum) => {
        const notification = cartNotification[cartStatus];
        if (hasExternalCartNotification || isTabVisible || !optedIn) {
            return;
        }

        dispatch(sendPushNotificationAction({
            title: intl.formatMessage({ id: `${notification.prefix}Title` }, { firstName }),
            text: intl.formatMessage({ id: `${notification.prefix}Text` }),
            ttl: notification.ttl > 0 ? notification.ttl : undefined,
            cartStatus,
        }));
    }, [isEnabled, optedIn, firstName, isTabVisible, hasExternalCartNotification]);

    return triggerNotification;
};
